import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, errText, mediaUrl } from "../lib/api";
import { Avatar, CreatorName, formatNum, renderMentions } from "./common";
import { MentionInput } from "./MentionInput";
import { TipModal } from "./TipModal";
import { GifPicker } from "./GifPicker";
import { usePlayer } from "../context/PlayerContext";
import { Heart, MessageCircle, Repeat2, Bookmark, Coins, Send, Play, MapPin, ExternalLink, Music2, PlusCircle, ChevronLeft, ChevronRight, X, Tv } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNowStrict } from "date-fns";

function timeAgo(d) { try { return formatDistanceToNowStrict(new Date(d)) + " ago"; } catch { return ""; } }

export function PostCard({ post, onChange }) {
  const [p, setP] = useState(post);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [cText, setCText] = useState("");
  const [cGif, setCGif] = useState(null);
  const [gifOpen, setGifOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const [burst, setBurst] = useState(false);
  const scrollRef = useRef();
  const lastTap = useRef(0);
  const player = usePlayer();

  const onScroll = () => { const el = scrollRef.current; if (el && el.clientWidth) setIdx(Math.round(el.scrollLeft / el.clientWidth)); };
  const scrollByOne = (dir) => { const el = scrollRef.current; if (el) el.scrollBy({ left: dir * el.clientWidth, behavior: "smooth" }); };

  const act = async (fn, optimistic) => { optimistic(); try { await fn(); } catch (e) { toast.error(errText(e)); } };

  const like = () => act(() => api.post(`/posts/${p.id}/like`),
    () => setP((x) => ({ ...x, liked: !x.liked, like_count: x.like_count + (x.liked ? -1 : 1) })));
  const repost = () => act(() => api.post(`/posts/${p.id}/repost`),
    () => setP((x) => ({ ...x, reposted: !x.reposted, repost_count: x.repost_count + (x.reposted ? -1 : 1) })));
  const save = () => act(() => api.post(`/posts/${p.id}/save`),
    () => setP((x) => ({ ...x, saved: !x.saved, save_count: x.save_count + (x.saved ? -1 : 1) })));

  const doubleTapLike = () => {
    setBurst(true); setTimeout(() => setBurst(false), 750);
    if (!p.liked) like();
  };
  const shareToStory = async () => {
    try {
      await api.post("/stories", {
        media_url: p.media?.[0]?.url || null,
        media_type: "repost",
        text: p.text || "",
        shared_ref: { kind: "post", id: p.id, author: p.author, text: p.text || "", media_url: p.media?.[0]?.url || null, media_type: p.media?.[0]?.type || "image", media: p.media || [] },
      });
      toast.success("Shared to your VirtuStars Story");
      window.dispatchEvent(new Event("vs:refresh"));
    } catch (e) { toast.error(errText(e)); }
  };
  const onMediaTouchEnd = () => { const now = Date.now(); if (now - lastTap.current < 300) doubleTapLike(); lastTap.current = now; };
  const HeartBurst = () => (
    <AnimatePresence>
      {burst && (
        <motion.div className="pointer-events-none absolute inset-0 flex items-center justify-center z-20"
          initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: [0.6, 1.15, 1], opacity: [0, 1, 1] }} exit={{ opacity: 0, scale: 1.3 }} transition={{ duration: 0.5 }}>
          <Heart size={110} className="text-white fill-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.6)]" />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const openComments = async () => {
    setShowComments((s) => !s);
    if (comments === null) { const { data } = await api.get(`/posts/${p.id}/comments`); setComments(data); }
  };
  const addComment = async () => {
    if (!cText.trim() && !cGif) return;
    try { const { data } = await api.post(`/posts/${p.id}/comment`, { text: cText, gif_url: cGif });
      setComments((c) => [data, ...(c || [])]); setP((x) => ({ ...x, comment_count: x.comment_count + 1 })); setCText(""); setCGif(null); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <article className="surface rounded-2xl overflow-hidden card-hover" data-testid={`post-${p.id}`}>
      <div className="p-4 pb-3 flex items-center gap-3">
        <Link to={`/${p.author?.username}`}><Avatar src={p.author?.avatar_url} name={p.author?.display_name} size={42} /></Link>
        <div className="min-w-0 flex-1">
          <Link to={`/${p.author?.username}`} className="font-semibold text-sm hover:text-eblue"><CreatorName user={p.author} /></Link>
          <div className="text-xs text-muted-foreground">@{p.author?.username} · {timeAgo(p.created_at)}</div>
        </div>
        {p.author?.roles?.primary && <span className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full surface-2 text-silver">{p.author.roles.primary}</span>}
      </div>

      {p.text && <p className="px-4 pb-3 text-[15px] leading-relaxed whitespace-pre-wrap">{renderMentions(p.text)}</p>}

      {p.media?.length > 0 && (
        p.media.length === 1 ? (
          <div className="relative bg-black flex justify-center" onDoubleClick={doubleTapLike} onTouchEnd={onMediaTouchEnd} data-testid={`post-media-${p.id}`}>
            {p.shared_ref?.kind === "reel" && <Link to="/reels" className="absolute top-2 left-2 z-10 text-[11px] bg-black/70 rounded-full px-2.5 py-1 hover:text-eblue" data-testid={`post-reel-credit-${p.id}`}>Reel by @{p.shared_ref.author?.username}</Link>}
            {p.media[0].type === "video"
              ? <video src={mediaUrl(p.media[0].url)} controls playsInline preload="metadata" className="w-full max-h-[600px] object-contain" data-testid={`post-video-${p.id}`} />
              : <img src={mediaUrl(p.media[0].url)} alt="" className="w-full max-h-[600px] object-contain" loading="lazy" />}
            <HeartBurst />
          </div>
        ) : (
          <div className="relative bg-black" data-testid={`post-carousel-${p.id}`} onDoubleClick={doubleTapLike} onTouchEnd={onMediaTouchEnd}>
            <div ref={scrollRef} onScroll={onScroll} className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar">
              {p.media.map((m, i) => (
                <img key={i} src={mediaUrl(m.url)} alt="" className="w-full shrink-0 snap-center max-h-[600px] object-contain" loading="lazy" />
              ))}
            </div>
            <HeartBurst />
            <div className="absolute top-2.5 right-2.5 bg-black/65 rounded-full px-2.5 py-0.5 text-xs font-medium" data-testid={`post-carousel-count-${p.id}`}>{idx + 1}/{p.media.length}</div>
            {idx > 0 && (
              <button onClick={() => scrollByOne(-1)} data-testid={`post-carousel-prev-${p.id}`} className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 items-center justify-center transition-colors"><ChevronLeft size={20} /></button>
            )}
            {idx < p.media.length - 1 && (
              <button onClick={() => scrollByOne(1)} data-testid={`post-carousel-next-${p.id}`} className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 items-center justify-center transition-colors"><ChevronRight size={20} /></button>
            )}
            <div className="absolute bottom-2.5 left-0 right-0 flex justify-center gap-1.5">
              {p.media.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/45"}`} />)}
            </div>
          </div>
        )
      )}

      {p.music && (
        <button onClick={() => player.playTrack(p.music, [p.music])} className="mx-4 mb-3 flex items-center gap-3 surface-2 rounded-xl p-3 w-[calc(100%-2rem)] hover:border-eblue/40 border border-white/10 transition-colors">
          <img src={p.music.cover_url} className="w-12 h-12 rounded-lg object-cover" alt="" />
          <div className="text-left min-w-0"><div className="text-sm font-semibold truncate flex items-center gap-1"><Music2 size={13} className="text-eblue" />{p.music.title}</div><div className="text-xs text-muted-foreground truncate">{formatNum(p.music.stream_count)} streams</div></div>
          <Play size={18} className="ml-auto text-eblue" />
        </button>
      )}

      {p.video && (
        <Link to={`/video/${p.video.id}`} className="mx-4 mb-3 block surface-2 rounded-xl overflow-hidden border border-white/10 hover:border-eblue/40 transition-colors w-[calc(100%-2rem)]" data-testid={`post-video-${p.id}`}>
          <div className="relative aspect-video bg-black">
            {p.video.thumbnail_url && <img src={p.video.thumbnail_url} className="w-full h-full object-cover" alt="" loading="lazy" />}
            <span className="absolute inset-0 flex items-center justify-center"><span className="w-12 h-12 rounded-full btn-gold flex items-center justify-center"><Play size={20} /></span></span>
            <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wide bg-black/70 text-gold rounded px-1.5 py-0.5">VStar TV</span>
          </div>
          <div className="p-3"><div className="text-sm font-semibold truncate flex items-center gap-1"><Tv size={13} className="text-eblue" />{p.video.title}</div><div className="text-xs text-muted-foreground truncate">{formatNum(p.video.view_count || 0)} views</div></div>
        </Link>
      )}

      {p.event && (
        <div className="mx-4 mb-3 surface-2 rounded-xl overflow-hidden border border-white/10">
          {p.event.artwork_url && <img src={mediaUrl(p.event.artwork_url)} className="w-full h-32 object-cover" alt="" />}
          <div className="p-3"><div className="font-display font-semibold">{p.event.title}</div><div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={12} /> {p.event.world} · {p.event.venue}</div></div>
        </div>
      )}

      {p.link && (
        <a href={p.link} target="_blank" rel="noreferrer" className="mx-4 mb-3 flex items-center gap-3 surface-2 rounded-xl p-3 border border-white/10 hover:border-eblue/40 transition-colors w-[calc(100%-2rem)]">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-eblue/30 to-gold/20 flex items-center justify-center"><ExternalLink size={18} className="text-eblue" /></div>
          <div className="min-w-0"><div className="text-sm font-medium truncate">Virtual-world link</div><div className="text-xs text-muted-foreground truncate">{p.link}</div></div>
        </a>
      )}

      <div className="px-2 py-1.5 flex items-center justify-between border-t border-white/5">
        <button onClick={like} data-testid={`like-btn-${p.id}`} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${p.liked ? "text-rose-400" : "text-muted-foreground hover:text-rose-400"}`}>
          <Heart size={18} className={p.liked ? "fill-rose-400" : ""} /> {formatNum(p.like_count)}
        </button>
        <button onClick={openComments} data-testid={`comment-btn-${p.id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-eblue transition-colors">
          <MessageCircle size={18} /> {formatNum(p.comment_count)}
        </button>
        <button onClick={repost} data-testid={`repost-btn-${p.id}`} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${p.reposted ? "text-emerald-400" : "text-muted-foreground hover:text-emerald-400"}`}>
          <Repeat2 size={18} /> {formatNum(p.repost_count)}
        </button>
        <button onClick={() => setTipOpen(true)} data-testid={`tip-btn-${p.id}`} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-gold transition-colors">
          <Coins size={18} /> Tip
        </button>
        {p.author?.username && (
          <button onClick={shareToStory} data-testid={`post-story-btn-${p.id}`} className="px-3 py-2 rounded-lg text-muted-foreground hover:text-eblue transition-colors" title="Share to your VirtuStars Story">
            <PlusCircle size={18} />
          </button>
        )}
        <button onClick={save} data-testid={`save-btn-${p.id}`} className={`px-3 py-2 rounded-lg transition-colors ${p.saved ? "text-gold" : "text-muted-foreground hover:text-gold"}`}>
          <Bookmark size={18} className={p.saved ? "fill-[var(--gold)]" : ""} />
        </button>
      </div>

      {showComments && (
        <div className="border-t border-white/5 p-4 space-y-3">
          {cGif && (
            <div className="relative w-fit" data-testid={`comment-gif-preview-${p.id}`}>
              <img src={cGif} alt="" className="max-h-32 rounded-lg" />
              <button onClick={() => setCGif(null)} data-testid={`comment-gif-remove-${p.id}`} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center"><X size={13} /></button>
            </div>
          )}
          <div className="flex gap-2 items-center">
            <MentionInput value={cText} onChange={setCText} onEnter={addComment} multiline={false}
              placeholder="Add a comment… use @ to mention" className="flex-1 surface-2 border border-white/10 rounded-full px-4 py-2 text-sm outline-none" testid={`comment-input-${p.id}`} />
            <button onClick={() => setGifOpen(true)} data-testid={`comment-gif-btn-${p.id}`} title="Add a GIF" className="shrink-0 px-2.5 h-10 rounded-full surface-2 border border-white/10 text-[10px] font-bold tracking-wide text-eblue hover:border-eblue/50 transition-colors flex items-center">GIF</button>
            <button onClick={addComment} className="w-10 h-10 rounded-full btn-blue flex items-center justify-center shrink-0"><Send size={16} /></button>
          </div>
          {(comments || []).map((c) => (
            <div key={c.id} className="flex gap-2.5">
              <Avatar src={c.author?.avatar_url} name={c.author?.display_name} size={30} />
              <div className="surface-2 rounded-2xl px-3 py-2 flex-1 min-w-0">
                <Link to={`/${c.author?.username}`} className="text-xs font-semibold hover:text-eblue">{c.author?.display_name}</Link>
                {c.text && <p className="text-sm whitespace-pre-wrap">{renderMentions(c.text)}</p>}
                {c.gif_url && <img src={c.gif_url} alt="" className="mt-1.5 max-h-48 rounded-lg" loading="lazy" />}
              </div>
            </div>
          ))}
        </div>
      )}

      <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} onSelect={(url) => setCGif(url)} />

      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={p.author} refType="post" refId={p.id} />
    </article>
  );
}
